import React, { useState, useRef, useEffect } from 'react';
import JsonEditor from '../../components/JsonEditor';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import {
  GET_DEFAULT_OPERATION_DATA,
  TRANSFORM_QUERY,
  WORKFLOW_LIST,
  UPDATE_TRANSFORM_SPEC
} from '../../containers/graphQL/queries';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Button,
  DialogActions,
  Typography,
  TextField,
  Box,
  Select,
  MenuItem,
  InputLabel,
  DialogContentText,
  FormControl
} from '@material-ui/core';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { formatString } from '../../utils/helpers';

const useStyles = makeStyles(theme => ({
  textField: {
    width: '80%'
  },
  selectField: {
    width: '100%'
  }
}));

type workflowInfo = {
  workflowCode: string;
  workflowName: string;
  operations?: any;
};

type workflowObj = {
  workflow: workflowContentData;
};

type workflowContentData = {
  content: workflowInfo[];
};

const TransformGrid = (props: any) => {
  const defaultSpec = [{ spec: { '*': '&' }, operation: 'shift' }];
  const classes = useStyles();
  let { code } = useParams<any>();
  const { enqueueSnackbar } = useSnackbar();

  const workflowList = useQuery<workflowObj, {}>(WORKFLOW_LIST, {});

  const [joltTransform] = useMutation(TRANSFORM_QUERY);
  const [updateTransformSpec] = useMutation(UPDATE_TRANSFORM_SPEC);
  const [transformResult, setTransformResult] = useState({});
  const [data, setData] = useState<any>({});
  const [input, setInput] = useState({});
  const [operations, setOperations] = useState<any>({});

  const [spec, setSpec] = useState<any>(defaultSpec);
  const [originalSpec, setOriginalSpec] = useState(defaultSpec);

  const [discardDraftConfirmDialog, isDiscardDraftConfirmDialog] = React.useState<any>(false);
  const [draft, isDraft] = React.useState<any>(false);

  let [getDefaultOperationData, { data: defaultOperationData }] = useLazyQuery(GET_DEFAULT_OPERATION_DATA, {
    fetchPolicy: 'network-only'
  });

  const jsonInputEditorRef = useRef<any>();
  const jsonSpecEditorRef = useRef<any>();
  const jsonOutputEditorRef = useRef<any>();

  const setInputEditorRef = (instance: any) => {
    if (instance) {
      jsonInputEditorRef.current = instance.jsonEditor;
    } else {
      jsonInputEditorRef.current = null;
    }
  };

  const setSpecEditorRef = (instance: any) => {
    if (instance) {
      jsonSpecEditorRef.current = instance.jsonEditor;
    } else {
      jsonSpecEditorRef.current = null;
    }
  };

  const setOutputEditorRef = (instance: any) => {
    if (instance) {
      jsonOutputEditorRef.current = instance.jsonEditor;
    } else {
      jsonOutputEditorRef.current = null;
    }
  };

  useEffect(() => {
    if (jsonInputEditorRef && jsonInputEditorRef.current) {
      jsonInputEditorRef.current.set(input);
      jsonInputEditorRef.current.expandAll && jsonInputEditorRef.current.expandAll();
    }
    if (jsonSpecEditorRef && jsonSpecEditorRef.current) {
      jsonSpecEditorRef.current.set(spec);
      jsonSpecEditorRef.current.expandAll && jsonSpecEditorRef.current.expandAll();
    }
    if (jsonOutputEditorRef && jsonOutputEditorRef.current) {
      jsonOutputEditorRef.current.set(transformResult);
      jsonOutputEditorRef.current.expandAll && jsonOutputEditorRef.current.expandAll();
    }
  }, [input, spec, transformResult]);

  useEffect(() => {
    const operationList: any = {};
    const serviceNameFormObj = _.filter(
      workflowList.data?.workflow?.content || [],
      (o: any) => o.workflowCode === props.workflowCode
    );

    (serviceNameFormObj?.[0]?.operations || []).forEach((operation: any) => {
      operationList[operation.operationCode] = operation.operationName;
    });
    setOperations(operationList);
  }, [props.workflowCode, workflowList.data]);

  useEffect(() => {
    if (data.operationCode) {
      getDefaultOperationData({
        variables: { workflowCode: props.workflowCode, operationCode: data.operationCode }
      });
    }
  }, [data.operationCode, getDefaultOperationData, props.edit]);

  useEffect(() => {
    setInput(
      defaultOperationData?.GetDefaultOperationData?.data
        ? formatString(defaultOperationData.GetDefaultOperationData.data)
        : {}
    );
  }, [defaultOperationData]);

  useEffect(() => {
    if (jsonSpecEditorRef.current) {
      setSpec(formatString(jsonSpecEditorRef.current.getText()));
    }
  }, [defaultOperationData]);

  useEffect(() => {
    if (props.edit) {
      const obj = { ...props.specData };

      setOriginalSpec(obj?.spec && !_.isEmpty(obj.spec) ? formatString(obj.spec) : defaultSpec);

      if (obj?.draftSpec && !_.isEmpty(obj.draftSpec)) {
        setSpec(formatString(obj.draftSpec));
        isDraft(true);
      } else {
        setSpec(obj?.spec && !_.isEmpty(obj.spec) ? formatString(obj.spec) : defaultSpec);
        isDraft(false);
      }

      if (obj.operationCode) {
        var tempOperationCode = obj.operationCode;
        var opList = tempOperationCode.split('_');
        let opCode: string = '';
        var isOperationFound: boolean = false;
        (opList || []).forEach((op: any) => {
          opCode = opCode ? opCode + ('_' + op) : op;

          Object.keys(operations || {}).forEach((operationCode: any) => {
            if (operationCode === opCode) {
              obj.operationCode = operationCode;
              isOperationFound = true;
              return;
            }
          });
          if (isOperationFound) {
            return;
          }
        });
        if (!isOperationFound) {
          obj.eventType = obj.operationCode;
          return;
        }
        obj.eventType = tempOperationCode.replace(obj.operationCode, '');
        obj.eventType = obj.eventType.startsWith('_') ? obj.eventType.substring(1) : obj.eventType;
      }
      setData(obj);
    } else {
      setSpec(defaultSpec);
      isDraft(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.edit, props.specData]);

  const formSerializer = (data: any, headers: Headers) => {
    const searchParams = Object.keys(data)
      .map(key => {
        return encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
      })
      .join('&');

    headers.set('Content-Type', 'application/x-www-form-urlencoded');
    headers.set('Accept', '*/*');

    return { body: searchParams, headers };
  };

  const handleChange = () => {
    joltTransform({
      variables: {
        input: jsonInputEditorRef.current.getText(),
        spec: jsonSpecEditorRef.current.getText(),
        formSerializer: formSerializer
      }
    })
      .then(response1 => {
        setTransformResult(formatString(response1?.data?.joltTransform?.data || {}));
        enqueueSnackbar('Spec validated successfully !!', { variant: 'success', preventDuplicate: true });
        return response1;
      })
      .catch(error => {
        enqueueSnackbar('Unable to validate Transform Spec', { variant: 'error', preventDuplicate: true });
      });
    setInput(formatString(jsonInputEditorRef.current.getText()));
    setSpec(formatString(jsonSpecEditorRef.current.getText()));
  };

  const handleCancel = () => {
    props.handleDialogClose();
    setInput({});
    setSpec({});
    setTransformResult({});
    setData({});
  };

  const handleSaveSpec = (specOperation: String) => {
    var specObj;
    var draftSpecObj;
    if (specOperation === 'save') {
      specObj = jsonSpecEditorRef.current.getText();
      draftSpecObj = null;
    } else if (specOperation === 'saveDraft') {
      specObj = JSON.stringify(originalSpec);
      draftSpecObj = jsonSpecEditorRef.current.getText();
      var comments = data?.comments ? data.comments : '';
      if (!comments.startsWith('DRAFT')) {
        data.comments = 'DRAFT ' + (data?.comments ? data.comments : '');
      }
    } else if (specOperation === 'discardDraft') {
      isDiscardDraftConfirmDialog(false);
      specObj = JSON.stringify(originalSpec);
      draftSpecObj = null;
    }

    updateTransformSpec({
      variables: {
        operationCode: data.operationCode
          ? data.operationCode + (data.eventType ? '_' + data.eventType : '')
          : data.eventType
          ? data.eventType
          : '',
        workflowCode: props.workflowCode,
        participantCode: code,
        type: data?.type,
        comments: data?.comments,
        spec: specObj ? specObj : JSON.stringify(defaultSpec),
        draftSpec: draftSpecObj ? draftSpecObj : null,
        httpStatus: data.type === 'RESPONSE' ? data.httpStatus || 200 : '0',
        method: props.edit ? 'PUT' : 'POST'
      }
    })
      .then(response => {
        enqueueSnackbar('Spec Saved successfully !!', { variant: 'success', preventDuplicate: true });
        props.refetch();
      })
      .catch(err => {
        enqueueSnackbar('Unable to Add Transform Spec', { variant: 'error', preventDuplicate: true });
      });
    props.handleDialogClose();
  };

  const handleDataChange = (e: any, key: string) => {
    const specInfo = { ...data };
    specInfo[key] = e.target.value;
    setData(specInfo);
  };

  const handleDiscardDraftDialogClose = () => {
    isDiscardDraftConfirmDialog(false);
  };

  const handleDiscardDraftDialogSubmit = () => {
    isDiscardDraftConfirmDialog(true);
  };

  return (
    <div>
      <Dialog onClose={props.handleDialogClose} open={props.open} fullWidth={true} maxWidth="lg">
        <DialogTitle id="customized-dialog-title">
          {props.edit ? 'Edit Transform Spec' : 'Create Transform Spec'}
        </DialogTitle>
        <DialogContent>
          <div>
            <Box pb={2}>
              <Grid container>
                <Grid item xs={4}>
                  <FormControl required={true} className={classes.selectField}>
                    <InputLabel id="transform-spec-operationCode-label">Operation</InputLabel>
                    <Select
                      data-id="transform-spec-operationCode"
                      className={classes.textField}
                      label="Operation"
                      disabled={props.edit}
                      value={data?.operationCode || ''}
                      onChange={e => handleDataChange(e, 'operationCode')}
                    >
                      {Object.keys(operations || {}).map((key: any) => [
                        <MenuItem value={key}>{operations[key]}</MenuItem>
                      ])}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    data-id="transform-spec-additionalPath"
                    id="transform-spec-additionalPath"
                    label="Event Type"
                    disabled={props.edit}
                    className={classes.textField}
                    value={data?.eventType || ''}
                    onChange={e => handleDataChange(e, 'eventType')}
                  ></TextField>
                </Grid>
                <Grid item xs={4}>
                  <InputLabel id="demo-controlled-open-select-label">Type</InputLabel>
                  <Select
                    data-id="transform-spec-type"
                    className={classes.textField}
                    label="Type"
                    disabled={props.edit}
                    value={data?.type || 'EVENT'}
                    defaultValue="EVENT"
                    onChange={e => handleDataChange(e, 'type')}
                  >
                    <MenuItem value="EVENT">Event</MenuItem>
                    <MenuItem value="REQUEST">Request</MenuItem>
                    <MenuItem value="RESPONSE">Response</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={4}>
                  <InputLabel id="demo-controlled-open-select-label">Http Status</InputLabel>
                  <Select
                    data-id="transform-spec-httpStatus"
                    className={classes.textField}
                    label="Http Status"
                    disabled={props.edit || data.type !== 'RESPONSE'}
                    value={data?.httpStatus || (data.type !== 'RESPONSE' ? 0 : 200)}
                    onClick={e => handleDataChange(e, 'httpStatus')}
                  >
                    <MenuItem value="200">200</MenuItem>
                    <MenuItem value="202">202</MenuItem>
                    <MenuItem value="400">400</MenuItem>
                    <MenuItem value="409">409</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    data-id="transform-spec-comments"
                    id="transform-spec-comments"
                    label="Comments"
                    className={classes.textField}
                    value={data?.comments || ''}
                    onChange={e => handleDataChange(e, 'comments')}
                  ></TextField>
                </Grid>
              </Grid>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="button">Input</Typography>
                <JsonEditor
                  data-id="transform-input-editor"
                  setRef={setInputEditorRef}
                  data={input}
                  mode="code"
                  view={false}
                  id={'JsonEditorInput'}
                  allowedModes={['code', 'tree']}
                  theme="ace/theme/github"
                  htmlElementProps={{
                    style: {
                      height: '60vh',
                      width: '30vw'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                {draft ? (
                  <Typography variant="button">Spec (DRAFT)</Typography>
                ) : (
                  <Typography variant="button">Spec</Typography>
                )}
                <JsonEditor
                  data-id="transform-spec-editor"
                  setRef={setSpecEditorRef}
                  data={spec}
                  mode={props?.selectedKey === 'JOLT' ? 'code' : 'text'}
                  view={false}
                  id={'JsonEditorSpec'}
                  allowedModes={['code', 'tree']}
                  theme="ace/theme/github"
                  htmlElementProps={{
                    style: {
                      height: '60vh',
                      width: '30vw'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <Typography variant="button">Output</Typography>
                <JsonEditor
                  data-id="transform-output-editor"
                  setRef={setOutputEditorRef}
                  data={transformResult}
                  view
                  mode="code"
                  id={'JsonEditorOutput'}
                  theme="ace/theme/github"
                  htmlElementProps={{
                    style: {
                      height: '60vh',
                      width: '30vw'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </div>
        </DialogContent>
        <DialogActions>
          <Button data-id="cancel-Button" autoFocus onClick={handleCancel} color="primary" variant="outlined">
            Cancel
          </Button>
          {!props.supportUser && (
            <Button
              data-id="Discard-Draft-Button"
              autoFocus
              onClick={handleDiscardDraftDialogSubmit}
              color="primary"
              variant="contained"
              disabled={!props.edit}
            >
              Discard Draft
            </Button>
          )}
          {!props.supportUser && (
            <Button
              data-id="Validate-Button"
              variant="contained"
              color="primary"
              disableElevation
              onClick={handleChange}
            >
              Validate
            </Button>
          )}
          {!props.supportUser && (
            <Button
              data-id="Save-Button"
              autoFocus
              onClick={() => handleSaveSpec('save')}
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          )}
          {!props.supportUser && (
            <Button
              data-id="Save-Draft-Button"
              autoFocus
              onClick={() => handleSaveSpec('saveDraft')}
              color="primary"
              variant="contained"
            >
              Save Draft
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        data-id="Revert-spec-dialog"
        open={discardDraftConfirmDialog}
        onClose={handleDiscardDraftDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Revert Spec</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Do you want to Discard Draft spec?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDiscardDraftDialogClose} color="primary" variant="outlined">
            No
          </Button>
          <Button
            data-id="revert-dialog-submit"
            onClick={() => handleSaveSpec('discardDraft')}
            color="primary"
            variant="contained"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
export default TransformGrid;
